/**
 * Suivi global des pointeurs pour les tooltips des boutons (useLongPressTooltip).
 *
 * Le long press ne peut pas se fier aux seuls événements reçus par le bouton :
 * plusieurs situations font qu'un doigt se lève sans que le bouton reçoive son
 * `pointerup` / `pointercancel` / `pointerleave` (bouton passé en
 * `pointer-events: none` par `.drawing-active` pendant l'appui, bouton
 * désactivé, capture du pointeur perdue après un re-rendu…). Le minuteur
 * survivait alors au doigt levé : le tooltip s'ouvrait « tout seul » 800 ms plus
 * tard, et restait affiché faute d'événement pour le refermer.
 *
 * Ce module écoute donc les pointeurs au niveau de `window`, en phase de
 * capture (avant tout `stopPropagation`), et expose :
 *  - l'état réel « ce pointeur est-il encore posé ? » ;
 *  - un abonnement au relâchement de n'importe quel pointeur, où qu'il ait lieu ;
 *  - un abonnement à tout nouvel appui, pour refermer un tooltip resté ouvert ;
 *  - l'activité récente du stylet, pour ignorer la paume posée en écrivant.
 *
 * Installé une seule fois, au premier bouton monté, et jamais retiré : quelques
 * listeners passifs sur window, sans coût mesurable.
 */

/**
 * Délai pendant lequel un appui tactile est considéré comme une paume après un
 * contact du stylet : en écrivant, la main repose sur l'écran et touche souvent
 * les barres d'outils.
 */
export const PEN_PALM_GUARD_MS = 1000;

/** Identifiant passé aux abonnés quand TOUS les pointeurs sont relâchés d'office. */
export const ALL_POINTERS = -1;

const downPointers = new Map<number, string>();
let lastPenContactAt = Number.NEGATIVE_INFINITY;

const releaseHandlers = new Set<(pointerId: number) => void>();
const downHandlers = new Set<(event: PointerEvent) => void>();

let installed = false;

function notifyRelease(pointerId: number) {
  releaseHandlers.forEach((handler) => handler(pointerId));
}

function onWindowPointerDown(event: PointerEvent) {
  downPointers.set(event.pointerId, event.pointerType);
  if (event.pointerType === "pen") lastPenContactAt = performance.now();
  downHandlers.forEach((handler) => handler(event));
}

function onWindowPointerRelease(event: PointerEvent) {
  downPointers.delete(event.pointerId);
  if (event.pointerType === "pen") lastPenContactAt = performance.now();
  notifyRelease(event.pointerId);
}

/**
 * Perte de focus de la page (changement d'app, écran verrouillé) : les
 * relâchements en cours ne seront jamais distribués. On considère tout relâché.
 */
function releaseAll() {
  downPointers.clear();
  notifyRelease(ALL_POINTERS);
}

function onVisibilityChange() {
  if (document.visibilityState === "hidden") releaseAll();
}

export function installTooltipPointerTracker(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const options = { capture: true, passive: true } as const;
  window.addEventListener("pointerdown", onWindowPointerDown, options);
  window.addEventListener("pointerup", onWindowPointerRelease, options);
  window.addEventListener("pointercancel", onWindowPointerRelease, options);
  window.addEventListener("blur", releaseAll);
  document.addEventListener("visibilitychange", onVisibilityChange);
}

/** Le pointeur est-il toujours en contact avec l'écran ? */
export function isPointerDown(pointerId: number): boolean {
  return downPointers.has(pointerId);
}

/** Un stylet touche l'écran, ou vient de le quitter. */
export function isPenRecentlyActive(now = performance.now()): boolean {
  for (const type of downPointers.values()) {
    if (type === "pen") return true;
  }
  return now - lastPenContactAt < PEN_PALM_GUARD_MS;
}

/** Appelé au relâchement de n'importe quel pointeur, où qu'il ait lieu. */
export function onAnyPointerRelease(
  handler: (pointerId: number) => void,
): () => void {
  releaseHandlers.add(handler);
  return () => releaseHandlers.delete(handler);
}

/** Appelé à chaque nouvel appui, n'importe où dans la page. */
export function onAnyPointerDown(
  handler: (event: PointerEvent) => void,
): () => void {
  downHandlers.add(handler);
  return () => downHandlers.delete(handler);
}

/**
 * Le pointeur principal de l'appareil sait-il survoler ? Vrai sur un ordinateur
 * (souris, pavé tactile), faux sur une tablette — même quand un stylet ou un
 * clavier à pavé tactile y est connecté. Le survol n'ouvre un tooltip que dans le
 * premier cas : sur tablette, certains stylets signalent leur survol comme une
 * souris, ce qui ouvrait des tooltips sans aucun contact.
 */
export function primaryPointerCanHover(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/** Réinitialise l'état interne. Réservé aux tests. */
export function resetTooltipPointerTrackerForTests(): void {
  downPointers.clear();
  lastPenContactAt = Number.NEGATIVE_INFINITY;
}
